import path from 'node:path'
import {fileURLToPath} from 'node:url'
import {isMainThread, Worker} from 'node:worker_threads'

import {type CliCommandContext} from '@sanity/cli'
import readPkgUp from 'read-pkg-up'
import {createSchema} from 'sanity'

import {
  type ResolvedGraphQLAPI,
  type ResolvedSourceProperties,
  type SchemaDefinitionish,
  type TypeResolvedGraphQLAPI,
} from './types'

const __dirname = path.dirname(fileURLToPath(import.meta.url))

export async function getGraphQLAPIs(cliContext: CliCommandContext): Promise<ResolvedGraphQLAPI[]> {
  if (!isMainThread) {
    throw new Error('getGraphQLAPIs() must be called from the main thread')
  }

  const defaultSchema = createSchema({name: 'default', types: []})
  const defaultTypes = defaultSchema.getTypeNames()
  const isCustomType = (type: SchemaDefinitionish) => !defaultTypes.includes(type.name)

  const apis = await getApisWithSchemaTypes(cliContext)
  const resolved = apis.map(
    ({schemaTypes, ...api}): ResolvedSourceProperties => ({
      schema: createSchema({name: 'default', types: schemaTypes.filter(isCustomType)}),
      ...api,
    }),
  )

  return resolved
}

function getApisWithSchemaTypes(cliContext: CliCommandContext): Promise<TypeResolvedGraphQLAPI[]> {
  return new Promise<TypeResolvedGraphQLAPI[]>((resolve, reject) => {
    const {cliConfig, cliConfigPath, workDir} = cliContext
    const rootPkgPath = readPkgUp.sync({cwd: __dirname})?.path
    if (!rootPkgPath) {
      throw new Error('Could not find root directory for `sanity` package')
    }

    const rootDir = path.dirname(rootPkgPath)
    const workerPath = path.join(
      rootDir,
      'lib',
      '_internal',
      'cli',
      'threads',
      'getGraphQLAPIs.cjs',
    )
    const worker = new Worker(workerPath, {
      workerData: {cliConfig: serialize(cliConfig || {}), cliConfigPath, workDir},
      env: process.env,
    })
    worker.on('message', resolve)
    worker.on('error', reject)
    worker.on('exit', (code) => {
      if (code !== 0) reject(new Error(`Worker stopped with exit code ${code}`))
    })
  })
}

function serialize<T>(obj: T): T {
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (cause) {
    throw new Error(`Failed to serialize CLI configuration`, {cause})
  }
}
